/**
 * Rich text sanitisation — DESIGN.md §17.
 *
 * Everything that arrives from a browser, an MCP agent, or the Asana importer
 * passes through here before it reaches the database. The rule is allowlist
 * only: node types, marks, and each type's attributes are enumerated, and
 * anything else is rejected rather than silently stripped — a description that
 * quietly loses half its content is worse than a 400.
 */
import { z } from 'zod';
import { richTextSchema, } from './schema.js';
export class RichTextError extends Error {
    path;
    constructor(message, path) {
        super(message);
        this.path = path;
        this.name = 'RichTextError';
    }
}
/** Schemes a link mark may use. `javascript:` and `data:` are the reason this exists. */
const SAFE_LINK_SCHEMES = ['http:', 'https:', 'mailto:', 'tel:'];
/** Per-node attribute schemas. A node type absent here accepts no attributes. */
const ATTR_SCHEMAS = {
    heading: z.object({ level: z.number().int().min(1).max(4) }),
    orderedList: z.object({ start: z.number().int().min(0).optional() }).partial(),
    taskItem: z.object({ checked: z.boolean().optional() }).partial(),
    codeBlock: z.object({ language: z.string().max(40).nullish() }).partial(),
    image: z.object({
        /** Attachment-backed only: `/api/v1/files/:id`. See §6.8. */
        src: z.string().max(2048),
        attachmentId: z.string().uuid().nullish(),
        alt: z.string().max(1000).nullish(),
        title: z.string().max(1000).nullish(),
        filename: z.string().max(500).nullish(),
        width: z.number().int().positive().nullish(),
        height: z.number().int().positive().nullish(),
    }),
    mention: z.object({
        /** `user` or `group` — both notify, differently (§6.9). */
        kind: z.enum(['user', 'group']),
        id: z.string().uuid(),
        label: z.string().min(1).max(200),
    }),
    cardRef: z.object({
        number: z.number().int().positive(),
        /** Resolved server-side; clients may omit it. */
        taskId: z.string().uuid().nullish(),
        title: z.string().max(500).nullish(),
    }),
    tableCell: z
        .object({
        colspan: z.number().int().positive().optional(),
        rowspan: z.number().int().positive().optional(),
        colwidth: z.array(z.number()).nullish(),
    })
        .partial(),
    tableHeader: z
        .object({
        colspan: z.number().int().positive().optional(),
        rowspan: z.number().int().positive().optional(),
        colwidth: z.array(z.number()).nullish(),
    })
        .partial(),
};
/** Nodes that hold inline content only — a `paragraph` inside `text` is malformed. */
const LEAF_NODES = new Set([
    'text',
    'hardBreak',
    'horizontalRule',
    'image',
    'mention',
    'cardRef',
]);
const DEFAULTS = {
    maxTextLength: 1_000_000,
    maxDepth: 20,
    maxNodes: 20_000,
};
/**
 * Validate and normalise a document. Returns a new object containing only
 * allowlisted structure — safe to persist as-is.
 */
export function sanitizeDoc(input, options = {}) {
    const opts = { ...DEFAULTS, ...options };
    const parsed = richTextSchema.safeParse(input);
    if (!parsed.success) {
        throw new RichTextError(`Not a valid rich text document: ${parsed.error.issues[0]?.message ?? 'unknown shape'}`, parsed.error.issues[0]?.path.join('.') ?? 'doc');
    }
    const state = { nodes: 0, textLength: 0 };
    const content = (parsed.data.content ?? []).map((node, i) => sanitizeNode(node, `doc.content[${i}]`, 1, opts, state));
    return { type: 'doc', content };
}
function sanitizeNode(node, path, depth, opts, state) {
    if (depth > opts.maxDepth)
        throw new RichTextError('Document is nested too deeply', path);
    if (++state.nodes > opts.maxNodes)
        throw new RichTextError('Document has too many nodes', path);
    if (node.type === 'doc')
        throw new RichTextError('Nested doc nodes are not allowed', path);
    const out = { type: node.type };
    // Attributes: allowlisted per node type.
    const attrSchema = ATTR_SCHEMAS[node.type];
    if (attrSchema) {
        const result = attrSchema.safeParse(node.attrs ?? {});
        if (!result.success) {
            throw new RichTextError(`Invalid attributes on <${node.type}>: ${result.error.issues[0]?.message ?? 'unknown'}`, path);
        }
        const attrs = result.data;
        if (node.type === 'image')
            assertSafeUrl(String(attrs.src ?? ''), path, ['http:', 'https:'], true);
        out.attrs = stripNullish(attrs);
    }
    else if (node.attrs && Object.keys(node.attrs).length > 0) {
        throw new RichTextError(`<${node.type}> does not accept attributes`, path);
    }
    // Text.
    if (node.type === 'text') {
        const text = node.text ?? '';
        if (text === '')
            throw new RichTextError('Empty text node', path);
        state.textLength += text.length;
        if (state.textLength > opts.maxTextLength) {
            throw new RichTextError('Document text is too long', path);
        }
        out.text = text;
    }
    else if (node.text !== undefined) {
        throw new RichTextError(`<${node.type}> cannot carry text`, path);
    }
    // Marks — only on text nodes, which is what ProseMirror itself guarantees.
    if (node.marks?.length) {
        if (node.type !== 'text')
            throw new RichTextError(`<${node.type}> cannot carry marks`, path);
        out.marks = node.marks.map((mark, i) => sanitizeMark(mark, `${path}.marks[${i}]`));
    }
    // Children.
    if (node.content?.length) {
        if (LEAF_NODES.has(node.type)) {
            throw new RichTextError(`<${node.type}> cannot have children`, path);
        }
        out.content = node.content.map((child, i) => sanitizeNode(child, `${path}.content[${i}]`, depth + 1, opts, state));
    }
    return out;
}
function sanitizeMark(mark, path) {
    if (mark.type !== 'link')
        return { type: mark.type };
    const href = withScheme(mark.attrs.href);
    assertSafeUrl(href, path, SAFE_LINK_SCHEMES, false);
    // Outbound links always open safely; we set rel ourselves rather than trusting input.
    return { type: 'link', attrs: { href, target: '_blank', rel: 'noopener noreferrer nofollow' } };
}
/**
 * `www.example.com/a` → `https://www.example.com/a` — card #73.
 *
 * Autolinking and pasted HTML both produce hrefs with no scheme, and `new URL`
 * refuses those, so the *whole document* was rejected with "Invalid URL" over
 * one link that every browser would have opened. Assuming https is what every
 * editor does with a bare host, and it is the safe assumption rather than the
 * permissive one: the alternative schemes are the dangerous ones, and they are
 * still refused below because they have to be written out to be used.
 *
 * Deliberately narrow. Only something that already looks like a host gets a
 * scheme; anything else falls through and is rejected exactly as before.
 */
const SCHEMELESS_HOST = /^(?:[\w-]+\.)+[a-z]{2,}(?:[:/?#]|$)/i;
function withScheme(href) {
    if (href.startsWith('/') || /^[a-z][a-z0-9+.-]*:/i.test(href))
        return href;
    return SCHEMELESS_HOST.test(href) ? `https://${href}` : href;
}
/**
 * Reject anything that is not an allowlisted absolute URL or a same-origin
 * relative path. Relative paths matter: attachment images are stored as
 * `/api/v1/files/:id` so they keep working if the host name changes.
 */
function assertSafeUrl(value, path, schemes, relativeOnly) {
    if (value.startsWith('/') && !value.startsWith('//'))
        return;
    if (relativeOnly) {
        // Images may still point at an absolute https URL (e.g. an imported Asana asset).
        if (!/^https?:\/\//i.test(value)) {
            throw new RichTextError('Image src must be a relative path or an http(s) URL', path);
        }
    }
    let url;
    try {
        url = new URL(value);
    }
    catch {
        throw new RichTextError(`Invalid URL: ${truncate(value)}`, path);
    }
    if (!schemes.includes(url.protocol)) {
        throw new RichTextError(`Unsupported URL scheme "${url.protocol}"`, path);
    }
}
function stripNullish(attrs) {
    return Object.fromEntries(Object.entries(attrs).filter(([, v]) => v !== null && v !== undefined));
}
function truncate(value, max = 60) {
    return value.length > max ? `${value.slice(0, max)}…` : value;
}
//# sourceMappingURL=sanitize.js.map